import React from 'react'
import {Offer, UnitGroupOffer} from '../../types'
import SearchUnitAttributes from './SearchUnitAttributes'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {
  faBoltLightning,
  faCaretRight,
  faCaretUp
} from '@fortawesome/free-solid-svg-icons'
import SearchUnitOfferItem from './SearchUnitOfferItem'
import ImageCarousel from '../common/ImageCarousel'
import SearchUnitDetailsModal from './SearchUnitDetailsModal'
import clsx from 'clsx'
import {getLogger} from '../../services/logging'
import {useAppSelector} from '../../store/hooks'
import {
  selectPropertiesById,
  selectUnitGroupsById
} from '../../store/slices/configSlice'
import {pushDataLayerEvent} from '../../services/dataLayer'
import {selectSearchNightCount} from '../../store/slices/searchParamsSlice'
import SaveMoneyTime from '../../assets/save-moneytime-cabana.svg'

const logger = getLogger('SearchUnitResultItem')

export interface SearchUnitResultItemProps {
  propertyId: string
  unitGroupOffer: UnitGroupOffer
}

const AVAILABLE_UNITS_THRESHOLD = 5
const DISPLAY_OFFER_THRESHOLD = 3

function getAvailableUnitsLabel(availableUnits: number): string | undefined {
  if (availableUnits === 1) {
    return 'Only 1 room left'
  } else if (availableUnits <= AVAILABLE_UNITS_THRESHOLD) {
    return `Only ${availableUnits} rooms left`
  } else {
    return
  }
}

const SearchUnitResultItem = ({
  propertyId,
  unitGroupOffer
}: SearchUnitResultItemProps) => {
  const [showDetailsModal, setShowDetailsModal] = React.useState(false)
  const [showAllOffers, setShowAllOffers] = React.useState(false)
  const propertyLookup = useAppSelector(selectPropertiesById)
  const propertyConfig = propertyLookup[propertyId]
  const {unitGroup, availableUnits, offers} = unitGroupOffer
  const allOffers = offers.sort((a: Offer, b: Offer) => {
    if (a.totalGrossAmount.amount < b.totalGrossAmount.amount) {
      return -1
    } else if (a.totalGrossAmount.amount > b.totalGrossAmount.amount) {
      return 1
    } else {
      return 0
    }
  })
  const hasCollapsibleOffers = allOffers.length > DISPLAY_OFFER_THRESHOLD
  const displayOffers =
    hasCollapsibleOffers && showAllOffers
      ? allOffers
      : allOffers.slice(0, DISPLAY_OFFER_THRESHOLD)
  const allOffersCount = allOffers.length
  const unitGroupConfigsById = useAppSelector(selectUnitGroupsById)
  const unitGroupConfig = unitGroupConfigsById[unitGroup.id]
  const nightCount = useAppSelector(selectSearchNightCount)

  const handleShowDetailsModal = () => {
    pushDataLayerEvent({
      event: 'Unit_Group_Details_View',
      category: 'IBE',
      value: 1
    })
    logger.info({
      message: 'Viewing room details modal...',
      data: {unitGroupOffer}
    })
    setShowDetailsModal(true)
  }

  const handleToggleViewAllOffers = () => {
    pushDataLayerEvent({
      event: 'View_All_Offers',
      category: 'IBE',
      value: 1
    })
    logger.info({
      message: 'Viewing all offers...',
      data: {unitGroupOffer}
    })
    setShowAllOffers(!showAllOffers)
  }

  return (
    <>
      <div className="card bg-white shadow" data-unit-group-id={unitGroup?.id}>
        <div className="flex flex-col md:flex-row">
          {/* image carousel */}
          <div
            className={clsx(
              'w-full overflow-hidden rounded-tl-2xl rounded-tr-2xl h-72',
              'md:w-5/12 md:rounded-tr-none md:h-auto md:max-h-[600px]'
            )}
          >
            <ImageCarousel images={unitGroupConfig?.images} />
          </div>

          <div className="w-full md:w-7/12">
            {/* unit header */}
            <div className="w-full flex flex-col gap-1 p-4 pb-3">
              {/* unit group title */}
              <h1 className="text-xl font-semibold">{unitGroup.name}</h1>

              {/* property name/city */}
              <div className="text-sm text-light-primary text-pretty mb-2">
                {propertyConfig.name} &#x2022; {propertyConfig.location}{' '}
              </div>

              {/* unit group attributes */}
              <SearchUnitAttributes
                unitAttributes={unitGroupConfig?.attributes}
                className="mb-1"
              />

              {/* unit group description */}
              {unitGroupConfig?.description && (
                <div className="text-sm text-light-primary">
                  {unitGroupConfig?.description}
                </div>
              )}

              {/* unit group details modal */}
              <a
                className={clsx(
                  'link text-sm text-accent font-semibold',
                  'no-underline hover:underline'
                )}
                onClick={handleShowDetailsModal}
                onKeyDown={(e: React.KeyboardEvent<HTMLAnchorElement>) => {
                  if (e.key === 'Enter') {
                    handleShowDetailsModal()
                  }
                }}
                tabIndex={0}
                aria-label="View room details."
              >
                View room details
              </a>
            </div>

            <div className="divider my-0 mx-4" />

            {/* unit low availability warning */}
            {availableUnits < AVAILABLE_UNITS_THRESHOLD && (
              <div className="flex flex-row justify-end px-4">
                <div className="text-error text-sm font-medium">
                  <FontAwesomeIcon
                    icon={faBoltLightning}
                    className="mr-1"
                    title="Low availability warning"
                  />
                  <span>{getAvailableUnitsLabel(availableUnits)}</span>
                </div>
              </div>
            )}

            <div className="w-full flex flex-col gap-2 px-4 pb-3">
              {/* extended stay banner - weekly */}
              {nightCount >= 5 && nightCount < 7 && (
                <div className="rounded-lg bg-secondary/40 text-sm p-2">
                  <div className="mx-auto flex flex-row gap-2 justify-center items-center">
                    <img
                      src={SaveMoneyTime}
                      alt="Save money time"
                      className="h-6 w-6 opacity-50"
                    />
                    <div className="flex flex-col xl:flex-row">
                      <span className="font-semibold">
                        Stay 7+ nights and save 15%
                      </span>
                      <span className="font-light mx-0.5 hidden xl:block">
                        |
                      </span>
                      <span className="font-light">
                        Try different dates for better rates.
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* extended stay banner - monthly */}
              {nightCount >= 20 && nightCount < 28 && (
                <div className="rounded-lg bg-secondary/40 text-sm p-2">
                  <div className="mx-auto flex flex-row gap-2 justify-center items-center">
                    <img
                      src={SaveMoneyTime}
                      alt="Save money time"
                      className="h-6 w-6 opacity-50"
                    />
                    <div className="flex flex-col xl:flex-row">
                      <span className="font-semibold">
                        Stay 28+ nights and save 30%
                      </span>
                      <span className="font-light mx-0.5 hidden xl:block">
                        |
                      </span>
                      <span className="font-light">
                        Try different dates for better rates.
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* unit offers */}
              {displayOffers.map(offer => {
                return (
                  <div
                    className="rounded-lg bg-gray-100 p-3"
                    key={offer?.ratePlan?.id}
                  >
                    <SearchUnitOfferItem
                      offer={offer}
                      propertyId={propertyId}
                    />
                  </div>
                )
              })}
            </div>
            {hasCollapsibleOffers && (
              <div className="flex flex-row justify-end items-center pt-0 pb-3 px-3">
                <a
                  className="link font-semibold no-underline hover:underline"
                  onClick={handleToggleViewAllOffers}
                  onKeyDown={(e: React.KeyboardEvent<HTMLAnchorElement>) => {
                    if (e.key === 'Enter') {
                      handleToggleViewAllOffers()
                    }
                  }}
                  tabIndex={0}
                  aria-label="Toggle view all offers."
                >
                  <FontAwesomeIcon
                    icon={showAllOffers ? faCaretUp : faCaretRight}
                    className="mr-1 text-xs"
                    title="Toggle featured offers"
                  />
                  <span className="text-accent text-sm ">
                    {showAllOffers
                      ? 'View featured rates only'
                      : `View all ${allOffersCount} rates`}
                  </span>
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      <SearchUnitDetailsModal
        propertyId={propertyId}
        unitGroup={unitGroupOffer?.unitGroup}
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false)
        }}
      />
    </>
  )
}

export default SearchUnitResultItem
